import {
  Flex,
  Heading,
  Box,
  SimpleGrid,
  Button,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../shared/Icons';
import { useVdcConfiguration, VdcFormValues } from './useVdcConfiguration';
import { VdcPricing } from '@texas/api/endpoints/vdcApi';
import { useDebouncedCallback } from 'use-debounce';
import { formatRange } from './utils';

export function PreviewPane({
  formValues,
  disabled,
  selectedBranches,
  onAdd,
}: {
  formValues: VdcFormValues;
  selectedBranches: number[];
  disabled?: boolean;
  onAdd?: () => void;
}) {
  const { t } = useTranslation();
  const {
    templateRangeTiers,
    templates,
    branchRegions,
    treatments,
    calculatePricing,
    loadingPricing,
  } = useVdcConfiguration();

  const [previewPricing, setPreviewPricing] = useState<VdcPricing | null>(null);

  const onChange = useDebouncedCallback(async () => {
    if (
      !formValues.productGroupId ||
      !selectedBranches.length ||
      !formValues.tierRangeId ||
      !formValues.compositionGroupId ||
      !formValues.width ||
      !formValues.length
    ) {
      setPreviewPricing(null);
      return;
    }

    const response = await calculatePricing({
      productGroupId: formValues.productGroupId,
      branchIds: selectedBranches,
      tierRangeId: formValues.tierRangeId,
      templateId: formValues.compositionGroupId,
      labelWidth: formValues.width,
      labelHeight: formValues.length,
    });

    if (!response.hasError) {
      setPreviewPricing(response.data);
    }
  }, 300);

  const values = useMemo(() => {
    const tier = templateRangeTiers?.find(
      (t) => t.id === formValues.tierRangeId,
    );
    const template = templates?.find(
      (t) => t.id === formValues.compositionGroupId,
    );
    const regions = branchRegions?.filter((br) =>
      br.branches.some((b) => selectedBranches.includes(b.id)),
    );
    const treatmentsFiltered =
      treatments
        ?.filter((t) => formValues.treatmentIds?.includes(t.id))
        .map((t) => t.name) ?? [];

    return {
      tier,
      template,
      regions,
      treatments: treatmentsFiltered,
    };
  }, [
    templateRangeTiers,
    templates,
    branchRegions,
    treatments,
    formValues.tierRangeId,
    formValues.compositionGroupId,
    formValues.treatmentIds,
    selectedBranches,
  ]);

  useEffect(() => {
    onChange();
  }, [onChange, values, formValues.width, formValues.length]);

  if (!values.template && !values.tier && !values.regions) {
    return null;
  }

  return (
    <Flex
      pos="sticky"
      bottom={0}
      left={0}
      right={0}
      flexDir="column"
      gap={2}
      p={4}
      bg="texas.bg.800"
      _light={{ bg: 'gray.50' }}
    >
      <Heading fontSize="sm">{t('vdc.selected')}</Heading>
      <Flex gap={2} alignItems="center">
        <Icons.PackageVariantClosed boxSize={6} />
        <Text fontSize="sm">
          {values.tier
            ? formatRange(values.tier.startRange, values.tier.endRange)
            : t('vdc.noneSelected')}
        </Text>
      </Flex>
      <Flex gap={2} alignItems="center">
        <Icons.TextureBox boxSize={6} />
        <Text fontSize="sm">
          {values.template
            ? !values.template.note || values.template.note.length == 0
              ? t('compositionGroup.option.optionNr', {
                  nr: values.template.version,
                })
              : values.template.note
            : t('vdc.noneSelected')}
        </Text>
      </Flex>
      <Flex gap={2} alignItems="center">
        <Icons.RulerSquare boxSize={6} />
        <Text fontSize="sm">{formValues.width * formValues.length} mm2</Text>
      </Flex>
      <Flex gap={2} alignItems="center">
        <Icons.CogOutline boxSize={6} />
        {values.treatments.length > 0 ? (
          <Text fontSize="sm">{values.treatments.join(', ')}</Text>
        ) : (
          <Text fontSize="sm">{t('vdc.noTreatmentsSelected')}</Text>
        )}
      </Flex>
      <Box p={2} bg="texas.bg.800" _light={{ bg: 'gray.100' }}>
        {values.regions?.length === 0 && (
          <Text>{t('vdc.noRegionsSelected')}</Text>
        )}
        <SimpleGrid columns={values.regions?.length ?? 1} spacing={4}>
          {values.regions?.map((region) => {
            const regionPrice = previewPricing?.regionPrices.find(
              (rp) => rp.regionId === region.id,
            );
            return (
              <Flex key={region.id} gap={1} fontSize="sm" flexDir="column">
                <Text fontWeight="bold">{region.name}</Text>
                {loadingPricing ? (
                  <Spinner size="sm" />
                ) : regionPrice ? (
                  <Text>{regionPrice.salePrice} USD</Text>
                ) : (
                  <Text color="gray.500">—</Text>
                )}
              </Flex>
            );
          })}
        </SimpleGrid>
      </Box>
      {onAdd && (
        <Button isDisabled={disabled} onClick={onAdd}>
          {t('vdc.addToPricingTable')}
        </Button>
      )}
    </Flex>
  );
}
